/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2026 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

const MEDDREAM_READY_ACTION = "COMMUNICATION_SERVICE_READY";
const DEFAULT_READY_TIMEOUT_MS = 60_000;

// Only one pending wait at a time: a newer open cancels the older one so its
// tokens can never be posted into a later viewer session.
let cancelPending: ((reason: string) => void) | null = null;

// Kept on purpose so QA can follow the listener lifecycle in the console.
const log = (message: string) => {
  // eslint-disable-next-line no-console
  console.log(`[MEDDREAM]: ${message}`);
};

/**
 * Registers a one-shot window "message" listener that calls `onReady` when MedDream
 * (from `origin`) reports COMMUNICATION_SERVICE_READY. The listener is removed after it
 * fires, after `timeoutMs`, or when this function is called again.
 * Returns a function that cancels the wait.
 */
export const waitForMeddreamReady = (
  origin: string,
  onReady: () => void,
  timeoutMs = DEFAULT_READY_TIMEOUT_MS
): (() => void) => {
  cancelPending?.("replaced by a new open");

  const controller = new AbortController();
  const stop = (reason: string) => {
    if (controller.signal.aborted) return;
    controller.abort();
    if (cancelPending === stop) cancelPending = null;
    log(`READY listener removed (${reason}).`);
  };
  const timer = setTimeout(
    () => stop(`timeout after ${timeoutMs}ms`),
    timeoutMs
  );
  controller.signal.addEventListener("abort", () => clearTimeout(timer));

  window.addEventListener(
    "message",
    ({ data, origin: messageOrigin }: MessageEvent) => {
      // Browsers without `signal` support (Safari/iOS < 15) never remove the
      // listener, so ignore events once the wait is over.
      if (
        controller.signal.aborted ||
        messageOrigin !== origin ||
        data?.actionType !== MEDDREAM_READY_ACTION
      ) {
        return;
      }
      stop("READY received");
      onReady();
    },
    { signal: controller.signal }
  );
  cancelPending = stop;
  log(`READY listener added for ${origin} (timeout ${timeoutMs}ms).`);

  return () => stop("cancelled");
};

/**
 * Cancels the pending READY wait, if any. Call before every MedDream open so extra
 * tokens of an earlier multi-patient open are never posted into the new viewer.
 */
export const cancelPendingMeddreamReady = (): void => {
  if (!cancelPending) {
    log("No pending READY listener to clear.");
    return;
  }
  cancelPending("cleared before a new open");
};
